// Admin — Driver devices (Batch 13b). Rollout view: which drivers have the
// Dispatch app, which version, does push work, when did location last arrive.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Smartphone } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Table, type Column } from '../../../components/ui/Table/Table';
import { Badge } from '../../../components/ui/Badge/Badge';
import { useDriverDevices } from './hooks/useDriversAdmin';
import type { DriverDevice } from '../../../api/drivers';
import styles from './DriverDevicesPage.module.css';

function formatWhen(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DriverDevicesPage(): JSX.Element {
  const navigate = useNavigate();
  const q = useDriverDevices();
  const drivers = q.data?.drivers ?? [];
  const minimum = q.data?.minimum_version ?? null;

  const summary = useMemo(() => {
    const seen = drivers.filter((d) => d.app_version !== null).length;
    return {
      total: drivers.length,
      seen,
      notSeen: drivers.length - seen,
      outdated: drivers.filter((d) => d.below_minimum === true).length,
      noPush: drivers.filter((d) => !d.push_registered).length,
    };
  }, [drivers]);

  const columns: Column<DriverDevice>[] = useMemo(
    () => [
      { key: 'driver_name', header: 'Driver' },
      {
        key: 'app_version',
        header: 'App version',
        render: (_v, row) => {
          if (row.app_version === null) return <Badge variant="muted">Not installed / not seen</Badge>;
          return row.below_minimum === true ? (
            <Badge variant="danger">{row.app_version} - below minimum</Badge>
          ) : (
            <Badge variant="success">{row.app_version}</Badge>
          );
        },
      },
      {
        key: 'platform',
        header: 'Platform',
        render: (_v, row) =>
          row.platform === null
            ? '—'
            : `${row.platform === 'ios' ? 'iOS' : 'Android'}${row.os_version ? ` ${row.os_version}` : ''}`,
      },
      {
        key: 'last_seen_at',
        header: 'Last seen',
        render: (_v, row) => formatWhen(row.last_seen_at),
      },
      {
        key: 'push_registered',
        header: 'Push',
        render: (_v, row) =>
          row.push_registered ? <Badge variant="success">Yes</Badge> : <Badge variant="warning">No</Badge>,
      },
      {
        key: 'last_location_at',
        header: 'Last location ping',
        render: (_v, row) => formatWhen(row.last_location_at),
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Driver devices"
        subtitle={
          minimum !== null
            ? `Minimum supported app version: ${minimum}`
            : 'No minimum app version is set'
        }
        actions={
          <>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/admin/drivers')}>
              Drivers
            </Button>
            <Button variant="secondary" icon={RefreshCw} onClick={() => void q.refetch()}>
              Refresh
            </Button>
          </>
        }
      />

      <div className={styles.summary} data-testid="devices-summary">
        <Badge variant="info">{summary.total} active drivers</Badge>
        <Badge variant="success">{summary.seen} have the app</Badge>
        <Badge variant={summary.notSeen > 0 ? 'warning' : 'muted'}>{summary.notSeen} not seen yet</Badge>
        <Badge variant={summary.outdated > 0 ? 'danger' : 'muted'}>{summary.outdated} below minimum</Badge>
        <Badge variant={summary.noPush > 0 ? 'warning' : 'muted'}>{summary.noPush} without push</Badge>
      </div>

      <Card flush>
        <Table
          columns={columns}
          data={drivers}
          keyField={(row) => row.driver_uuid}
          loading={q.isLoading}
          emptyState={
            <div className={styles.empty}>
              <Smartphone size={28} aria-hidden />
              <h3>No active drivers</h3>
            </div>
          }
          ariaLabel="Driver devices"
        />
      </Card>

      <p className={styles.note}>
        Versions are reported by the app each time it talks to the server, so a driver who has not
        opened the app since installing it shows as &ldquo;not seen&rdquo;. Updates every minute.
      </p>
    </div>
  );
}
